/**
 * Store Popup Component
 *
 * Renders store popup content
 * following Single Responsibility Principle
 */

import React, {useMemo} from 'react';
import {Popup} from 'react-map-gl';
import {MapPin, Phone, User} from 'lucide-react';
import {Store} from '../../../features/stores/models/store.model';
import {MapConfigurationService} from '../../../Services/map/MapConfigurationService';

export interface StorePopupProps {
  store?: Store;
  onClose: () => void;
  className?: string;
}

export const StorePopup: React.FC<StorePopupProps> = ({
  store,
  onClose,
  className = ''
}) => {
  const mapConfig = MapConfigurationService.getInstance();

  const popup = useMemo(() => {
    if (!store) {
      return null;
    }

    // Validate coordinates for popup
    const isValidCoordinates = mapConfig.validateCoordinates(
      store.latitude,
      store.longitude
    );

    if (!isValidCoordinates) {
      return null;
    }

    return (
      <Popup
        longitude={store.longitude}
        latitude={store.latitude}
        anchor="top"
        onClose={onClose}
        className={`store-popup ${className}`}
        maxWidth="300px"
      >
        <div className="p-3">
          <div className="flex items-start gap-3">
            {store.profileImageUrl ? (
              <img
                src={store.profileImageUrl}
                alt={store.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-gray-500" />
              </div>
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">
                {store.name}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                {store.address}
              </p>

              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <User className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-700">{store.managerName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-700" dir="ltr">{store.phoneNumber}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">انقر لعرض التفاصيل الكاملة</span>
                  <span className="text-brand-600 font-medium">→</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  }, [store, onClose, className, mapConfig]);

  return popup;
};

export default StorePopup;
